import type { ModeStorage } from '../../../../../lib/portalReport/reportTheme';

// CLIENT PORTAL — which report the STAFF "view as client" preview shows.
//
// ⚠️⚠️ IT DEFAULTS TO THE CLIENT'S LIVE REPORT, AND THAT IS THE WHOLE POINT OF
// THE PAGE. Phase 1 of the old report's retirement (2026-09-01) made every
// portal user see the restyle, and this flag still defaulted OFF — so from that
// moment an AM opening "view as client" was shown something no client could see.
// `ClientReportPreview.tsx`'s own header names that as the thing it exists to
// prevent: *"an AM must never be unsure whether they are looking at their own
// view or the client's."*
//
// ⚠️⚠️ EVERY FALLBACK NOW FAVOURS THE CLIENT'S VIEW — absent key, null storage
// and a storage that THROWS all return true. A preview that quietly falls back
// to the retired report because localStorage was unavailable is the same defect
// in a rarer path, and the rarer path is the one nobody notices.
//
// ⚠️⚠️ THE STORAGE KEY WAS BUMPED, NOT JUST THE DEFAULT. The old key holds
// `'off'` in the browser of every staff member who tried the restyle while it
// was experimental and turned it back off. Inverting the default alone would
// leave exactly those people on the retired report — looking at a preview that
// misrepresents their client, behind a toggle they have no reason to
// re-examine. Renaming discards every stored value, so everyone lands on the new
// default and the toggle keeps working from there. Same technique as `NAMESPACE`
// in server/lib/creativeMediaUrl.ts.
//
// ⚠️ Not a silent override of somebody's choice: the old value answered a
// question ("do you want to try the restyle?") that no longer exists. What the
// toggle now asks is which of two reports you are looking at, and only one of
// them is real.
//
// ⚠️ It stays a STAFF-ONLY, per-browser preference and is deliberately not a
// server field — an AM can still look at the retired report while it exists.
// Which report a CLIENT gets is resolved server-side, in
// src/lib/portalReport/restyleFlag.ts, and this flag cannot reach it.
//
// ⚠️ Every access is wrapped — some privacy modes THROW on localStorage rather
// than returning null, and an unhandled throw would white-screen the preview.

/**
 * ⚠️ THE `-v2` IS LOAD-BEARING, not a version stamp. See the header: it is what
 * discards the stored `'off'` of every AM who turned the restyle off while it
 * was experimental. A test pins this exact name so the bump cannot be reverted
 * quietly, which would silently restore those people to the retired report.
 */
export const RESTYLE_FLAG_STORAGE_KEY = 'kyck-portal-report-restyle-v2';

/**
 * Does this staff member's preview show the client's live report?
 *
 * ⚠️ Only an explicit `'off'` says no. Anything else — absent, unreadable, a
 * value from some future write — resolves to the client's view.
 */
export function readRestyleFlag(storage: ModeStorage | null | undefined): boolean {
  if (!storage) return true;
  try {
    return storage.getItem(RESTYLE_FLAG_STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function writeRestyleFlag(storage: ModeStorage | null | undefined, on: boolean): void {
  if (!storage) return;
  try {
    storage.setItem(RESTYLE_FLAG_STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    /* ignore — a browser that refuses the write still gets a working toggle
       for this visit, and there is nothing the user could do about it. */
  }
}
